import { useState, useEffect, useCallback, useRef } from 'react';
import {
  wpApi,
  DashboardData,
  PerformanceData,
  OrdersData,
  FinanceFullData,
  ComplaintsResponse,
} from '../services/wordpressApi';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastRefresh: Date | null;
}

function useApiPolling<T>(
  fetcher: () => Promise<T>,
  intervalMs = 90_000, // 90s default
): UseApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await fetcher();
      if (mountedRef.current) {
        setData(result);
        setLastRefresh(new Date());
        setLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Błąd pobierania danych');
        setLoading(false);
      }
    }
  }, [fetcher]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    const interval = setInterval(fetchData, intervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchData, intervalMs]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh, lastRefresh };
}

export function useDashboard(intervalMs = 60_000): UseApiState<DashboardData> {
  return useApiPolling(wpApi.getDashboard, intervalMs);
}

export function usePerformance(intervalMs = 90_000): UseApiState<PerformanceData> {
  return useApiPolling(wpApi.getPerformance, intervalMs);
}

export function useOrderStats(days = 7, intervalMs = 90_000): UseApiState<OrdersData> {
  const fetcher = useCallback(() => wpApi.getOrders(days), [days]);
  return useApiPolling(fetcher, intervalMs);
}

export function useFinance(intervalMs = 90_000): UseApiState<FinanceFullData> {
  return useApiPolling(wpApi.getFinance, intervalMs);
}

export function useComplaints(status?: string, intervalMs = 120_000): UseApiState<ComplaintsResponse> {
  const fetcher = useCallback(() => wpApi.getComplaints(status), [status]);
  return useApiPolling(fetcher, intervalMs);
}
